import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Spin, Table, message, Image } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getBusinesuser, templetedata } from '../../../../utils/API';
import dummyImage from '../../../../assets/frontendassets/Logos/dummyuser.jpg';

interface Customer {
    _id: string;
    businessName: string;
    city: string;
    state: string;
    mobileNumber: string;
    isDeleted: boolean;
    galleryImage: string[];
    packageID: { isPremium: boolean }[];
}

interface TemplateData {
    businessId: string;
    templateData: { data: { templateId: string } } | null;
}

const Businessindex: React.FC = () => {
    const [customerData, setCustomerData] = useState<Customer[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [templatedata, setTemplateData] = useState<TemplateData[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const imageUrl = process.env.REACT_APP_IMAGE_URL + '/businesse/';
    const navigate = useNavigate();

    const authData = localStorage.getItem('authData');
    const userId = authData ? JSON.parse(authData).user._id : '';

    const fetchBusinessData = async (page: number) => {
        setLoading(true);
        try {
            const response: any = await getBusinesuser(userId, page, '', '');
            const allBusinesses = response.data.filter((business: Customer) => !business.isDeleted);

            setCustomerData(allBusinesses);
            setTotalPages(response.totalPages);

            // Fetch template data for businesses with premium packages
            const promises = allBusinesses
                .filter((business: any) => business.packageID[0]?.isPremium)
                .map(async (premiumBusiness: any) => {
                    try {
                        const templateResponse = await templetedata(premiumBusiness._id);
                        return {
                            businessId: premiumBusiness._id,
                            templateData: templateResponse?.data ? templateResponse : null, // Ensure consistent structure
                        };
                    } catch (error) {
                        console.error(`Error fetching template data for businessId ${premiumBusiness._id}:`, error);
                        return { businessId: premiumBusiness._id, templateData: null };
                    }
                });

            const templateResults = await Promise.all(promises);
            console.log('Fetched Template Data:', templateResults); // Debugging
            setTemplateData(templateResults);
        } catch (error: any) {
            console.error('Error fetching business data:', error);
            message.error('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBusinessData(currentPage);
    }, [currentPage]);

    const getTemplateId = (businessId: string): string | undefined => {
        const template = templatedata.find((item) => item.businessId === businessId);
        return template?.templateData?.data?.templateId; // Safe optional chaining
    };

    const columns = [
        {
            title: (
                <div style={{ fontWeight: 'bold', fontSize: '10px', color: '#a6a6a6' }}>
                    Images
                </div>
            ),
            dataIndex: 'galleryImage',
            key: 'galleryImage',
            render: (galleryImage: string[]) => (
                <div>
                    {galleryImage && galleryImage.length > 0 ? (
                        <Image
                            src={`${imageUrl}${galleryImage[0]}`}
                            alt="Thumbnail"
                            className="rounded-md"
                            style={{ width: '80px', height: '60px', marginRight: '5px' }}
                        />
                    ) : (
                        <Image
                            src={dummyImage}
                            alt="Thumbnail"
                            className="rounded-md"
                            style={{ width: '80px', height: '60px', marginRight: '5px' }}
                        />
                    )}
                </div>
            ),
        },
        {
            title: <div style={{ fontWeight: 'bold', fontSize: '10px', color: '#a6a6a6' }}>Name</div>,
            dataIndex: 'businessName',
            key: 'businessName',
        },
        {
            title: <div style={{ fontWeight: 'bold', fontSize: '10px', color: '#a6a6a6' }}>Location</div>,
            dataIndex: 'city',
            key: 'city',
            render: (_: any, record: Customer) => (
                <div>{record.city}, {record.state}</div>
            ),
        },
        {
            title: <div style={{ fontWeight: 'bold', fontSize: '10px', color: '#a6a6a6' }}>Contact</div>,
            dataIndex: 'mobileNumber',
            key: 'mobileNumber',
        },
        {
            title: <div style={{ fontWeight: 'bold', fontSize: '10px', color: '#a6a6a6' }}>Action</div>,
            dataIndex: 'action',
            key: 'action',
            render: (_: any, record: Customer) => {
                const templateId = getTemplateId(record._id);
                return (
                    <div className="d-flex">
                        {templateId ? (
                            <a
                                className="text-yellow-500 cursor-pointer hover:text-yellow-600"
                                onClick={() => navigate(`/backend/${record._id}/themes/${templateId}`)}
                            >
                                My Website
                            </a>
                        ) : (
                            <a
                                className="text-yellow-500 cursor-pointer hover:text-yellow-600"
                                onClick={() => navigate(`/business/${record._id}/mywebsite`)}
                            >
                                Themes
                            </a>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <Row className="m-2" align="middle">
                <Col xs={24} sm={24} md={9} xl={6} xxl={6} className="d-flex justify-content-start font-bold">
                    <h2 className="text-2xl">Businesses</h2>
                </Col>
            </Row>
            <Row>
                <Col style={{ backgroundColor: '#ffffff', borderRadius: '12px' }} xs={24} sm={24} md={24} xl={24} xxl={24}>
                    <Card className="bg-white border" style={{ borderRadius: '12px' }}>
                        <Col xs={24} sm={24} md={24} xl={24} xxl={24}>
                            <Spin spinning={loading}>
                                <div style={{ overflowX: 'auto' }}>
                                    <Table
                                        columns={columns}
                                        dataSource={customerData}
                                        rowKey="_id"
                                        scroll={{ x: true }}
                                        pagination={{
                                            current: currentPage,
                                            total: totalPages * 7,
                                            onChange: (page) => setCurrentPage(page),
                                        }}
                                    />
                                </div>
                            </Spin>
                        </Col>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default Businessindex;
